import { useId, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import clsx from 'clsx';
import { useRouteRef } from '@backstage/core-plugin-api';
import { Alert, Flex, Skeleton, Text } from '@backstage/ui';
import { RiArrowRightSLine, RiHomeLine } from '@remixicon/react';
import { DOC_TYPE_LIST } from '../../types';
import { createTechdocsRootRef } from '../../routes';
import { useDocsView } from '../../hooks/useDocsView';
import { useReaderDocType } from '../../hooks/useReaderDocType';
import { useLegacyDocsNav } from '../../hooks/useLegacyDocsNav';
import { useLegacyDocsLocation } from '../../hooks/useLegacyDocsLocation';
import { LEGACY_DOCS_TITLE } from '../../utils/docCategory';
import { getCategoryPath } from '../../utils/docsView';
import { findNavTrail } from '../../utils/legacyDocsNav';
import type { NavNode } from '../../utils/parseMkdocsNav';
import styles from './DocsSidebar.module.css';

const SKELETON_ROW_COUNT = 6;

/**
 * The docs sidebar of every docs page: the docs home, the doc categories, and
 * the nav tree of the legacy Aura docs site.
 */
export function DocsSidebar() {
  const { pathname } = useLocation();
  const getTechdocsRootPath = useRouteRef(createTechdocsRootRef);
  const docsRootPath = getTechdocsRootPath();
  const docsView = useDocsView();
  // On a reader page, the doc type of the page marks its category.
  const readerDocType = useReaderDocType();
  const activeDocType = docsView?.type ?? readerDocType;
  const { basePath: legacyBasePath, pagePath: legacyPagePath } =
    useLegacyDocsLocation();
  const isDocsHome =
    pathname.replace(/\/+$/, '') === docsRootPath && !activeDocType;

  return (
    <nav aria-label="Docs" className={styles.root}>
      <ul className={styles.list}>
        <li>
          <SidebarLink
            to={docsRootPath}
            icon={<RiHomeLine size={16} aria-hidden />}
            isCurrent={isDocsHome}
          >
            Docs home
          </SidebarLink>
        </li>
      </ul>

      <SidebarSection title="Categories">
        <ul className={styles.list}>
          {DOC_TYPE_LIST.map(({ type, title, icon: Icon }) => (
            <li key={type}>
              <SidebarLink
                to={getCategoryPath(docsRootPath, type)}
                icon={<Icon size={16} aria-hidden />}
                isCurrent={activeDocType === type}
              >
                {title}
              </SidebarLink>
            </li>
          ))}
        </ul>
      </SidebarSection>

      <SidebarSection
        title={
          // The site home is not in the tree, so the section title links to it.
          <Link
            to={legacyBasePath}
            className={styles.sectionTitleLink}
            aria-current={legacyPagePath === '' ? 'page' : undefined}
          >
            {LEGACY_DOCS_TITLE}
          </Link>
        }
      >
        <LegacyDocsNav basePath={legacyBasePath} pagePath={legacyPagePath} />
      </SidebarSection>
    </nav>
  );
}

function SidebarSection({
  title,
  children,
}: {
  title: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className={styles.section}>
      <Text
        as="h2"
        variant="body-large"
        weight="bold"
        color="primary"
        className={styles.sectionTitle}
      >
        {title}
      </Text>
      {children}
    </section>
  );
}

function SidebarLink({
  to,
  icon,
  isCurrent,
  depth = 0,
  children,
}: {
  to: string;
  icon?: ReactNode;
  isCurrent: boolean;
  depth?: number;
  children: ReactNode;
}) {
  return (
    <Link
      to={to}
      className={styles.item}
      style={getDepthStyle(depth)}
      aria-current={isCurrent ? 'page' : undefined}
    >
      {icon}
      <span className={styles.label}>{children}</span>
    </Link>
  );
}

function LegacyDocsNav({
  basePath,
  pagePath,
}: {
  basePath: string;
  pagePath: string | undefined;
}) {
  const { data: nav, isPending, error } = useLegacyDocsNav();

  if (isPending) {
    return (
      <Flex direction="column" gap="2" className={styles.skeleton}>
        {Array.from({ length: SKELETON_ROW_COUNT }, (_, index) => (
          <Skeleton key={index} width="80%" height={20} />
        ))}
      </Flex>
    );
  }

  if (error) {
    return (
      <Alert
        status="danger"
        icon
        title="Could not load the Aura docs navigation"
        description={error.message}
      />
    );
  }

  if (nav.length === 0) {
    return (
      <Alert
        status="warning"
        icon
        title="Could not read the Aura docs navigation"
        description="The home page of the site has no navigation menu in a format that the portal can read."
      />
    );
  }

  const activeTrail =
    pagePath === undefined ? [] : (findNavTrail(nav, pagePath) ?? []);

  return (
    <ul className={styles.list}>
      {nav.map((node, index) => (
        <NavTreeItem
          key={`${index}-${node.label}`}
          node={node}
          depth={0}
          basePath={basePath}
          pagePath={pagePath}
          activeTrail={activeTrail}
        />
      ))}
    </ul>
  );
}

function NavTreeItem({
  node,
  depth,
  basePath,
  pagePath,
  activeTrail,
}: {
  node: NavNode;
  depth: number;
  basePath: string;
  pagePath: string | undefined;
  /** The groups above the open page, and the page. */
  activeTrail: NavNode[];
}) {
  const listId = useId();
  const holdsOpenPage = activeTrail.includes(node);
  // A click overrides the open state until the open page moves into or out of
  // this group. So a group opens when you go to a page inside it, also from a
  // link in a doc, and a group that you opened stays open while you read
  // elsewhere. The state does not need to survive a reload.
  const [override, setOverride] = useState<{
    isOpen: boolean;
    holdsOpenPage: boolean;
  }>();
  const isOpen =
    override?.holdsOpenPage === holdsOpenPage ? override.isOpen : holdsOpenPage;

  if (!node.children) {
    return node.path === undefined ? null : (
      <li>
        <SidebarLink
          to={getLegacyPageUrl(basePath, node.path)}
          icon={<ChevronSlot />}
          isCurrent={node.path === pagePath}
          depth={depth}
        >
          {node.label}
        </SidebarLink>
      </li>
    );
  }

  return (
    <li>
      <button
        type="button"
        className={clsx(styles.item, styles.group)}
        style={getDepthStyle(depth)}
        aria-expanded={isOpen}
        aria-controls={listId}
        onClick={() => setOverride({ isOpen: !isOpen, holdsOpenPage })}
      >
        <RiArrowRightSLine
          size={16}
          aria-hidden
          className={clsx(styles.chevron, isOpen && styles.chevronOpen)}
        />
        <span className={styles.label}>{node.label}</span>
      </button>
      {isOpen && (
        <ul id={listId} className={styles.list}>
          {node.path !== undefined && (
            // A group with its own page: the page comes first in the group.
            <li>
              <SidebarLink
                to={getLegacyPageUrl(basePath, node.path)}
                icon={<ChevronSlot />}
                isCurrent={node.path === pagePath}
                depth={depth + 1}
              >
                Overview
              </SidebarLink>
            </li>
          )}
          {node.children.map((child, index) => (
            <NavTreeItem
              key={`${index}-${child.label}`}
              node={child}
              depth={depth + 1}
              basePath={basePath}
              pagePath={pagePath}
              activeTrail={activeTrail}
            />
          ))}
        </ul>
      )}
    </li>
  );
}

/**
 * The empty chevron space of a legacy page. A group has its chevron here, so
 * the labels of groups and pages at the same depth line up.
 */
function ChevronSlot() {
  return <span aria-hidden className={styles.chevronSlot} />;
}

function getLegacyPageUrl(basePath: string, pagePath: string): string {
  return pagePath ? `${basePath}/${pagePath}` : basePath;
}

/** The indent of a nested item, as a custom property for the CSS module. */
function getDepthStyle(depth: number): CSSProperties {
  return { '--docs-sidebar-depth': depth } as CSSProperties;
}
